import React, { useState, ReactElement, Fragment } from 'react';
import styled from '@emotion/styled';

export interface ViewerTabsProps {
  children: ReactElement | ReactElement[];
  initialTabIndex?: number;
}

const TabHeaderContainer = styled.nav`
  width: 100%;
  height: 2.8125rem;
  background: #fff;
  display: flex;
`;

const NavItem = styled.div<{ selected: boolean }>`
  background-color: ${s => (s.selected ? '#F5F6FB' : '#fff')};
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
  border-right: 1px solid #e4e4e4;
  line-height: 1em;
  padding-right: 14px;
  font-size: 14px;
  cursor: pointer;
  color: #4694ce;
  font-weight: 600;
  text-transform: uppercase;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  border-bottom-color: ${p => (p.selected ? '#F5F6FB' : '#e4e4e4')};
`;

const NavBorder = styled.div`
  background: #fff;
  border-bottom: 1px solid #e4e4e4;
  flex: 1;
`;

const IconWrapper = styled.span`
  padding-left: 12px;
  padding-right: 8px;
`;

const TabContent = styled.div<{ selected: boolean }>`
  width: 100%;
  height: 100%;
  opacity: ${p => (p.selected ? 1 : 0)};
  height: ${p => (p.selected ? 'auto' : 0)};
  overflow-y: ${p => (p.selected ? 'unset' : 'hidden')};
`;

export interface TabsContextInterface {
  changeTab: Function;
  currentTabIndex: number;
}

const ViewerTabsContext = React.createContext<TabsContextInterface>({} as TabsContextInterface);

export const ViewerTabs = ({ children, initialTabIndex }: ViewerTabsProps) => {
  if (!children) {
    throw new Error('Secondary Tabs requires children of Tab type.');
  }

  if (!(children as ReactElement[]).length) {
    children = [children] as ReactElement[]; // allow one tab
  }

  const [currentTabIndex, setCurrentTabIndex] = useState(initialTabIndex || 0);

  const changeTab = (tabIndex: number) => {
    setCurrentTabIndex(tabIndex);
  };

  return (
    <Fragment>
      <ViewerTabsContext.Provider value={{ changeTab, currentTabIndex }}>
        <TabHeaderContainer>
          {React.Children.count(children) &&
            React.Children.map(children, (child, tabIndex) => {
              return (
                <NavItem
                  selected={currentTabIndex === tabIndex}
                  key={tabIndex}
                  onClick={() => {
                    changeTab(tabIndex);
                  }}
                >
                  <IconWrapper>{child && child.props.icon}</IconWrapper>
                  <span>{child && child.props.title}</span>
                </NavItem>
              );
            })}
          <NavBorder />
        </TabHeaderContainer>
        {React.Children.map(children, (child, tabIndex) => {
          return (
            <TabContent selected={currentTabIndex === tabIndex}>{child.props.children}</TabContent>
          );
        })}
      </ViewerTabsContext.Provider>
    </Fragment>
  );
};

export const useViewerTabsContext = () => {
  return React.useContext(ViewerTabsContext);
};

export default ViewerTabs;
